import { Modal } from "@circleco/compass/components/Modal";
import { TextInput } from "@circleco/compass/components/TextInput";
import type { ChangeEvent, KeyboardEvent } from "react";
import { useEffect, useRef, useState } from "react";

import { t } from "@/i18n-js/instance";

const MAX_TITLE_LENGTH = 255;

interface RenameConversationModalProps {
  confirmText: string;
  currentTitle: string | null;
  inputLabel: string;
  isOpen: boolean;
  isSaving: boolean;
  onCancel: () => void;
  onSave: (title: string) => void;
  placeholder: string;
  title: string;
}

export const RenameConversationModal = ({
  confirmText,
  currentTitle,
  inputLabel,
  isOpen,
  isSaving,
  onCancel,
  onSave,
  placeholder,
  title,
}: RenameConversationModalProps) => {
  const [value, setValue] = useState(currentTitle ?? "");
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    setValue(currentTitle ?? "");
    const frame = requestAnimationFrame(() => inputRef.current?.select());
    return () => cancelAnimationFrame(frame);
  }, [isOpen, currentTitle]);

  const trimmed = value.trim();
  const isUnchanged = trimmed === (currentTitle ?? "").trim();
  const isSaveDisabled = trimmed.length === 0 || isUnchanged || isSaving;

  const handleConfirm = () => {
    if (isSaveDisabled) return;
    onSave(trimmed);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter" && !isSaveDisabled) {
      event.preventDefault();
      handleConfirm();
    }
  };

  return (
    <Modal.Root
      open={isOpen}
      initialFocus={inputRef}
      onOpenChange={(open: boolean) => {
        if (!open) {
          onCancel();
        }
      }}
    >
      <Modal.Content>
        <Modal.Header title={title} showCloseButton={!isSaving} />
        <Modal.Body>
          <TextInput
            ref={inputRef}
            name="conversation-title"
            label={inputLabel}
            value={value}
            maxLength={MAX_TITLE_LENGTH}
            placeholder={placeholder}
            onChange={(event: ChangeEvent<HTMLInputElement>) =>
              setValue(event.target.value)
            }
            onKeyDown={handleKeyDown}
          />
        </Modal.Body>
        <Modal.Footer
          secondaryAction={
            isSaving
              ? undefined
              : {
                  label: t("cancel"),
                  onClick: onCancel,
                  shouldCloseModal: false,
                }
          }
          primaryAction={{
            label: confirmText,
            variant: "primary",
            disabled: isSaveDisabled,
            loading: isSaving,
            onClick: handleConfirm,
            type: "button",
          }}
        />
      </Modal.Content>
    </Modal.Root>
  );
};
